import { ReactNode, useCallback } from 'react';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { Box, Button, Toolbar } from '@mui/material';
import { csCZ, DataGrid, DataGridProps } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useDataGridStatePersistor } from '../../hooks/useMuiDataGridStatePersist';
import { useTableStateWatcher } from '../../hooks/useTableStateWatcher';

export type TableProps = DataGridProps & {
  id: string; // id of the table to persist state
  isOverviewMode?: boolean;
  toolbarContent?: ReactNode;
};

export const Table = ({ sx, id, isOverviewMode, toolbarContent, ...rest }: TableProps) => {
  const { apiRef } = useDataGridStatePersistor(id);
  const { updateState, isTableStateChanged } = useTableStateWatcher(isOverviewMode);
  const { t } = useTranslation();
  const restoreTableState = useCallback(() => {
    apiRef.current?.restoreState({
      sorting: { sortModel: [] },
      filter: { filterModel: { items: [] } },
      pagination: { paginationModel: { page: 0 } },
    });
    apiRef.current?.setRowSelectionModel([]);
  }, [apiRef]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, position: 'relative', width: '100%' }}>
      {!isOverviewMode && isTableStateChanged ? (
        <Toolbar variant={'dense'}>
          <Button color={'warning'} startIcon={<SettingsBackupRestoreIcon />} onClick={restoreTableState}>
            {t('restoreView')}
          </Button>
          {toolbarContent}
        </Toolbar>
      ) : null}
      <DataGrid
        apiRef={apiRef}
        sx={{
          bgcolor: 'background.paper',
          '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          ...sx,
        }}
        disableColumnSelector
        disableDensitySelector
        onStateChange={updateState}
        localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
        hideFooter={isOverviewMode}
        paginationModel={
          isOverviewMode
            ? {
                page: 0,
                pageSize: 5,
              }
            : undefined
        }
        {...rest}
      />
    </Box>
  );
};
